/**
 * The URL the user was trying to reach when their session turned out to be dead.
 *
 * The sign-in button on <NotAuthorized /> normally just uses the current address — the
 * 401 screen renders in place, so the address bar still holds the page they wanted. This
 * store covers the cases where that is no longer true by the time they click: a mid-session
 * 401 that arrives after they have navigated on, or a return trip through the DCC login.
 *
 * sessionStorage, not localStorage: it is scoped to the tab and dies with it, so a stale
 * target from last week can never hijack a fresh sign-in.
 */
const KEY = 'comply.attemptedUrl';

/** Paths that are not worth returning to — landing back on them just repeats the exit. */
const NON_RETURNABLE = ['/login', '/register', '/logout-clear'];

export function rememberAttemptedUrl(url?: string): void {
  if (typeof window === 'undefined') return;
  const target = url || window.location.href;
  try {
    const { pathname } = new URL(target, window.location.origin);
    if (NON_RETURNABLE.includes(pathname)) return;
    window.sessionStorage.setItem(KEY, target);
  } catch {
    /* unparseable — nothing worth remembering */
  }
}

/** The remembered URL, or the current one if nothing was stored. */
export function readAttemptedUrl(): string {
  if (typeof window === 'undefined') return '/';
  try {
    return window.sessionStorage.getItem(KEY) || window.location.href;
  } catch {
    return window.location.href;
  }
}

export function clearAttemptedUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
